import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Bell, Send, Loader2, Sparkles } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { API } from '../lib/api';
import axios from 'axios';

export const PropertyAlertsSection = ({ t, lang }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    email: '', name: '', property_type: '', max_price: '', min_bedrooms: '', location: ''
  });
  const [loading, setLoading] = useState(false);

  const labels = {
    sq: {
      badge: 'Njoftime Ekskluzive',
      title: 'Mos Humb Asnjë Pronë të Re',
      subtitle: 'Regjistrohu dhe merr njoftim menjëherë kur del një pronë që përputhet me kriteret e tua',
      email: 'Email-i juaj',
      name: 'Emri (opsional)',
      type: 'Tipi i pronës',
      typeAll: 'Të gjitha',
      typeSale: 'Për Shitje',
      typeRent: 'Për Qira',
      maxPrice: 'Buxheti maksimal (€)',
      bedrooms: 'Dhoma minimale',
      location: 'Lokacioni (p.sh. Prishtinë)',
      submit: 'Aktivizo Njoftimet',
      submitting: 'Duke ruajtur...',
      success: 'Regjistrimi u bë me sukses!',
      successDesc: 'Do t\'ju dërgojmë email sa herë të dalë një pronë që përputhet me kriteret tuaja.',
      error: 'Gabim',
    },
    en: {
      badge: 'Exclusive Alerts',
      title: 'Never Miss a New Property',
      subtitle: 'Sign up and get notified instantly when a property matching your criteria hits the market',
      email: 'Your email',
      name: 'Name (optional)',
      type: 'Property type',
      typeAll: 'All',
      typeSale: 'For Sale',
      typeRent: 'For Rent',
      maxPrice: 'Max budget (€)',
      bedrooms: 'Min bedrooms',
      location: 'Location (e.g. Pristina)',
      submit: 'Enable Alerts',
      submitting: 'Saving...',
      success: 'Successfully subscribed!',
      successDesc: 'We\'ll email you whenever a property matching your criteria is listed.',
      error: 'Error',
    }
  };
  const L = labels[lang];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        email: form.email,
        name: form.name || null,
        property_type: form.property_type || null,
        max_price: form.max_price ? parseFloat(form.max_price) : null,
        min_bedrooms: form.min_bedrooms ? parseInt(form.min_bedrooms) : null,
        location: form.location || null,
      };
      await axios.post(`${API}/alerts/subscribe`, payload);
      toast({ title: L.success, description: L.successDesc });
      setForm({ email: '', name: '', property_type: '', max_price: '', min_bedrooms: '', location: '' });
    } catch (err) {
      toast({
        title: L.error,
        description: (err.response?.data?.detail || 'Something went wrong').toString(),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="alerts" className="py-32 bg-black relative overflow-hidden">
      {/* Radiating background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-[#C5A05E]/20 via-[#C5A05E]/5 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 space-y-6">
          <div className="inline-flex items-center gap-3 px-6 py-2 bg-[#C5A05E]/10 backdrop-blur-sm border border-[#C5A05E]/30 rounded-full text-[#C5A05E] text-sm font-semibold uppercase tracking-wider animate-fadeInUp">
            <Sparkles className="w-4 h-4" />
            {L.badge}
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            {L.title}
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            {L.subtitle}
          </p>
        </div>

        <div className="relative animate-fadeInUp" style={{ animationDelay: '0.3s', opacity: 0, animationFillMode: 'forwards' }}>
          {/* Ambient glow */}
          <div className="absolute -inset-2 bg-gradient-to-r from-[#C5A05E]/30 via-[#D4B06E]/20 to-[#C5A05E]/30 rounded-3xl blur-2xl" />
          
          <Card className="relative backdrop-blur-2xl bg-white/5 border border-[#C5A05E]/30 overflow-hidden">
            <CardContent className="p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="alerts-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={L.email}
                    className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E] py-6 rounded-xl"
                    data-testid="alerts-email"
                  />
                  <Input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={L.name}
                    className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E] py-6 rounded-xl"
                    data-testid="alerts-name"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <select
                      value={form.property_type}
                      onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                      className="w-full bg-white/5 border border-white/20 text-white rounded-xl px-4 py-3.5 focus:border-[#C5A05E] focus:outline-none"
                      data-testid="alerts-type"
                    >
                      <option value="" className="bg-slate-900">{L.typeAll}</option>
                      <option value="sale" className="bg-slate-900">{L.typeSale}</option>
                      <option value="rent" className="bg-slate-900">{L.typeRent}</option>
                    </select>
                  </div>
                  <Input
                    type="number"
                    value={form.max_price}
                    onChange={(e) => setForm({ ...form, max_price: e.target.value })}
                    placeholder={L.maxPrice}
                    className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E] py-6 rounded-xl"
                    data-testid="alerts-price"
                  />
                  <Input
                    type="number"
                    value={form.min_bedrooms}
                    onChange={(e) => setForm({ ...form, min_bedrooms: e.target.value })}
                    placeholder={L.bedrooms}
                    className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E] py-6 rounded-xl"
                    data-testid="alerts-bedrooms"
                  />
                </div>

                <Input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder={L.location}
                  className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 focus:border-[#C5A05E] py-6 rounded-xl"
                  data-testid="alerts-location"
                />

                <Button
                  type="submit"
                  disabled={loading}
                  className="relative overflow-hidden w-full bg-gradient-to-r from-[#C5A05E] via-[#D4B06E] to-[#C5A05E] bg-[length:200%_100%] hover:bg-[position:100%_0] text-white font-bold shadow-2xl shadow-[#C5A05E]/50 transition-all duration-500 hover:scale-[1.02] hover:shadow-[#C5A05E]/70 group py-7 rounded-2xl border-2 border-[#C5A05E]/50"
                  size="lg"
                  data-testid="alerts-submit"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {L.submitting}</>
                  ) : (
                    <><Bell className="w-5 h-5 mr-2" /> {L.submit}</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
